// ─────────────────────────────────────────────────────────────────────────────
// DemoShowcase.jsx — the landing page for a demo session.
//
// Four tiles into the four real pages, each with a line saying what it proves.
// It is a launcher, not a mock: every tile navigates into the actual product,
// which is the whole point of serving fixtures through the network layer rather
// than building a parallel set of fake screens.
//
// The live counters are read from the same demo dataset the pages will show, so
// the numbers on this page and the numbers on the next page always agree.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays, ClipboardList, Fingerprint, SlidersHorizontal,
  ArrowRight, FlaskConical, Download,
} from 'lucide-react';
import {
  getData, subscribe, cacheAllPhotos, referencedServerPhotos, isCached,
} from '../../demo/demoStore';
import { isDemoMode } from '../../demo/installDemoAdapter';

const T = {
  bg: 'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  border: 'var(--nd-w06)',
  text: 'var(--nd-text)',
  strong: 'var(--nd-text-strong)',
  muted: 'var(--nd-tx40)',
  faint: 'var(--nd-tx35)',
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .ds-wrap { font-family: 'DM Sans', sans-serif; color: var(--nd-text); }
  .ds-tile {
    background: var(--nd-surface); border: 1px solid var(--nd-w06); border-radius: 16px;
    padding: 22px; cursor: pointer; text-align: left; width: 100%;
    transition: border-color .16s ease, transform .16s ease;
    display: flex; flex-direction: column; gap: 12px;
  }
  .ds-tile:hover { border-color: rgba(96,165,250,0.45); transform: translateY(-2px); }
  .ds-tile:focus-visible { outline: 2px solid rgba(96,165,250,0.6); outline-offset: 2px; }
  .ds-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 14px; }
  .ds-stat { font-family: 'DM Mono', monospace; font-size: 22px; font-weight: 500; letter-spacing: -0.02em; }
`;

const TILES = [
  {
    key: 'attendance',
    to: '/commercial/attendance',
    icon: Fingerprint,
    accent: '#22D3A5',
    title: 'Attendance',
    proves: 'Every shift is a GPS-stamped, photo-verified punch. Group by person, client or city, then switch to Coverage to see exactly who did not turn up — and who was simply working somewhere else that day.',
    stat: (d) => `${d.attendance.length} shifts`,
    sub: (d) => {
      const closed = d.attendance.filter((a) => a.clock_out_at);
      const hrs = closed.reduce(
        (n, a) => n + (Date.parse(a.clock_out_at) - Date.parse(a.clock_in_at)) / 3600000, 0,
      );
      return `${(hrs / closed.length).toFixed(1)}h average shift`;
    },
  },
  {
    key: 'calendar',
    to: '/commercial/team-calender',
    icon: CalendarDays,
    accent: '#60A5FA',
    title: 'Team scheduling',
    proves: 'A month of work across every client and crew on one grid. Man-hour billing status is reconciled against each client\'s contracted mandate, so "ready to invoice" is a number, not a judgement call.',
    stat: (d) => `${d.jobs.length} jobs`,
    sub: (d) => `${d.clients.length} clients · ${d.teams.length} crews`,
  },
  {
    key: 'history',
    to: '/commercial/job-history',
    icon: ClipboardList,
    accent: '#F59E0B',
    title: 'Job history',
    proves: 'Drill client to month to day to job. Every completed job carries before and after photos, the full field service report, panel counts and a customer signature — and exports as a PDF.',
    stat: (d) => `${d.jobs.filter((j) => j.status === 'completed').length} completed`,
    sub: (d) => {
      const p = d.jobs.reduce((n, j) => n + (j.total_panels_cleaned || 0), 0);
      return `${p.toLocaleString()} panels cleaned`;
    },
  },
  {
    key: 'mandate',
    to: '/commercial/mandate-config',
    icon: SlidersHorizontal,
    accent: '#A78BFA',
    title: 'Client mandates',
    proves: 'The contracted man-hours behind every billing decision, split by role. This is the input the scheduling calendar reconciles delivered work against.',
    stat: (d) => `${d.mandates.filter((m) => m.has_mandate).length} configured`,
    sub: (d) => {
      const unset = d.mandates.filter((m) => !m.has_mandate).length;
      return unset ? `${unset} still unset` : 'all clients set';
    },
  },
];

const DemoShowcase = () => {
  const navigate = useNavigate();
  // Re-render on any store change so the counters below never go stale after an
  // edit in the drawer. Everything here is derived inline rather than memoised:
  // it is 30 jobs and ~137 attendance rows, and this component renders rarely.
  const [, bump] = useState(0);
  useEffect(() => subscribe(() => bump((n) => n + 1)), []);

  const active = isDemoMode();
  const data = getData();

  // Photo caching. The uploads folders serve only full-size originals (0.8–4.7 MB,
  // no thumbnail, no cache headers), so an uncached demo shows blank photo panels
  // while the browser queues hundreds of megabytes. This banner is the prompt to
  // fix that, and it disappears once done — so it is only ever on screen during
  // prep, never in front of an audience.
  const [caching, setCaching] = useState(null);
  const allPhotos = referencedServerPhotos();
  const photoState = { total: allPhotos.length, cached: allPhotos.filter(isCached).length };
  const needsCache = active && photoState.total > 0 && photoState.cached < photoState.total;

  const warmCache = async () => {
    setCaching({ done: 0, total: photoState.total - photoState.cached, failed: 0 });
    try {
      await cacheAllPhotos((p) => setCaching(p));
    } finally {
      setCaching(null);
    }
  };

  return (
    <div className="ds-wrap" style={{ background: T.bg, padding: '14px 20px', minHeight: '100vh' }}>
      <style>{STYLES}</style>

      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: '24px 26px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 6 }}>
          <div style={{ width: 42, height: 42, borderRadius: 11, background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FlaskConical size={21} color="#60A5FA" />
          </div>
          <div>
            <div style={{ fontSize: 9, color: T.faint, letterSpacing: '0.18em', marginBottom: 4 }}>
              iNOVAA · C&amp;I OPERATIONS
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: T.strong, letterSpacing: '-0.025em', lineHeight: 1 }}>
              Product walkthrough
            </div>
          </div>
        </div>
        <div style={{ fontSize: 13, color: T.muted, maxWidth: 720, lineHeight: 1.6 }}>
          Four capabilities, shown on {data.clients.length} live commercial &amp; industrial accounts across{' '}
          {[...new Set(data.clients.map((c) => c.city))].join(', ')} — roughly ten weeks of field work.
          Each tile opens the real screen, not a picture of one.
        </div>

        {!active && (
          <div style={{ marginTop: 14, padding: '10px 13px', borderRadius: 9, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', fontSize: 12, color: '#F59E0B' }}>
            Demo mode is off, so these pages will load live production data instead.
            Sign in with the demo account, or append <code>?demo=1</code> to this URL.
          </div>
        )}

        {needsCache && (
          <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 9, background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.32)' }}>
            {caching ? (
              <>
                <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden', marginBottom: 8 }}>
                  <div style={{ width: `${caching.total ? (caching.done / caching.total) * 100 : 0}%`, height: '100%', background: '#60A5FA', transition: 'width .2s ease' }} />
                </div>
                <div style={{ fontSize: 12, color: '#60A5FA' }}>
                  Caching photo {caching.done} of {caching.total}
                  {caching.failed ? ` · ${caching.failed} failed` : ''} — stay on this page.
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 12, color: '#60A5FA', flex: '1 1 320px', lineHeight: 1.55 }}>
                  <strong>Prepare photos before demoing.</strong> The server stores only
                  full-size originals, so photos load slowly or not at all until they are
                  cached locally. One-time: {photoState.total - photoState.cached} images,
                  roughly {Math.round((photoState.total - photoState.cached) * 2.7)} MB down,
                  ~{Math.round((photoState.total - photoState.cached) * 0.15)} MB kept.
                  Afterwards the demo runs with no network at all.
                </div>
                <button
                  type="button"
                  onClick={warmCache}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(96,165,250,0.18)', border: '1px solid rgba(96,165,250,0.45)', color: '#60A5FA', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  <Download size={14} /> Cache photos
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="ds-grid">
        {TILES.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} type="button" className="ds-tile" onClick={() => navigate(t.to)}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: `${t.accent}1F`, border: `1px solid ${t.accent}44`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={19} color={t.accent} />
                </div>
                <ArrowRight size={16} color={T.faint} />
              </div>

              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: T.strong, letterSpacing: '-0.02em' }}>
                  {t.title}
                </div>
                <div className="ds-stat" style={{ color: t.accent, marginTop: 6 }}>{t.stat(data)}</div>
                <div style={{ fontSize: 11, color: T.faint, fontFamily: "'DM Mono', monospace", marginTop: 2 }}>
                  {t.sub(data)}
                </div>
              </div>

              <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>{t.proves}</div>
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: 16, fontSize: 11, color: T.faint, lineHeight: 1.6 }}>
        Data on these four pages is served locally for the walkthrough and is read-only —
        Save and Delete are intercepted, so nothing here can touch production.
      </div>
    </div>
  );
};

export default DemoShowcase;
